// Approval reminder steps, as stored: { type: 'immediate' | 'after_days' |
// 'days_before_start', days }. The server's rules (timeOffNotificationService):
// after_days counts from the day the request was submitted, not from the
// previous email, so the order of the steps never changes when anything goes out.

export const MAX_STEPS = 10;
export const MAX_DAYS = 365;
export const LEAD_OPTIONS = [5, 14, 30];

const dayWord = (n) => (n === 1 ? 'day' : 'days');

export const STEP_TYPES = {
  immediate: {
    label: 'Immediately',
    needsDays: false,
    suffix: () => 'when the request is submitted',
  },
  after_days: {
    label: 'After submittal',
    needsDays: true,
    suffix: (n) => `${dayWord(n)} after the request is submitted`,
  },
  days_before_start: {
    label: 'Before time off starts',
    needsDays: true,
    suffix: (n) => `${dayWord(n)} before the first day off`,
  },
  days_after_start: {
    label: 'After time off starts',
    needsDays: true,
    suffix: (n) => `${dayWord(n)} after the first day off`,
  },
};

export const SURVEY_STEP_TYPES = {
  days_before_close: {
    label: 'Before survey closes',
    needsDays: true,
    minDays: 0,
    suffix: (days) => days === 0 ? 'on the closing day' : `${dayWord(days)} before the closing day`,
  },
  after_sent: {
    label: 'After survey is sent',
    needsDays: true,
    suffix: (days) => `${dayWord(days)} after the survey is sent`,
  },
};

export const isValidDays = (step, types = STEP_TYPES) => Boolean(types[step.type]) && (!types[step.type].needsDays
  || (Number.isInteger(step.days) && step.days >= (types[step.type].minDays ?? 1) && step.days <= MAX_DAYS));

const SEND_DAY = {
  immediate: () => 0,
  after_days: (days) => days,
  days_before_start: (days, lead) => lead - days,
  days_after_start: (days, lead) => lead + days,
  days_before_close: (days, lead) => lead - days,
  after_sent: (days) => days,
};

// Send day relative to submission (day 0) for a request made `lead` days ahead.
// A reminder set to go after the start is meant to; only the others are late there.
export const scheduleReminders = (steps, lead) => steps.map((step, index) => {
  const day = SEND_DAY[step.type](step.days || 0, lead);
  const survey = step.type === 'days_before_close' || step.type === 'after_sent';
  return { ...step, index, day, late: survey ? day > lead : step.type !== 'days_after_start' && day >= lead, early: survey ? day <= 0 : day < 0 };
});

const listOf = (rows) => rows.map((row) => row.index + 1).join(', ');

// What the server will actually do with reminders that land outside the window.
export const scheduleWarnings = (rows, lead) => {
  const late = rows.filter((row) => row.late);
  const early = rows.filter((row) => row.early);
  const hasImmediate = rows.some((row) => row.type === 'immediate');
  const messages = [];
  if (late.length) {
    messages.push(`With ${lead} days’ notice, reminder ${listOf(late)} would go out after time off has started. It is still sent while the request is pending.`);
  }
  if (early.length) {
    messages.push(`Reminder ${listOf(early)} would fall before the request is submitted, so ${hasImmediate
      ? 'it is skipped (the immediate email covers it).'
      : 'it goes out with the next daily reminder run.'}`);
  }
  return messages.join(' ');
};
